import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { ConfirmPanel } from "@/components/Confirm";
import { ListSearch, useListSearch } from "@/components/ListSearch";
import {
  AlertTriangle,
  BotOff,
  ChevronLeft,
  Inbox,
  Layers,
  Lock,
  Pencil,
  Plus,
  Power,
  ShieldCheck,
  Tag,
  Trash2,
  Users,
} from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Permission, useCan } from "@/components/session-provider";
import { cn } from "@/lib/utils";
import { TeamMembers } from "./TeamMembers";
import { TeamLinksPanel } from "./TeamLinksPanel";
import { TeamSlaPanel } from "./TeamSlaPanel";
import {
  ErrorNote,
  LEVEL_DESCRIPTIONS,
  LevelChip,
  PanelSkeleton,
  SENSITIVITY_LEVELS,
  type AdminCategory,
  type AdminTeam,
  type SensitivityLevel,
} from "./shared";
import { Checkbox } from "@/components/ui/checkbox";

/**
 * Which destructive action is waiting on an answer.
 *
 * One piece of state rather than four, so opening a question anywhere closes the one that
 * was already open — two confirmations on screen at once is how the wrong one gets answered.
 */
type PendingAction = {
  kind: "close-team" | "delete-team" | "retire-category" | "delete-category";
  id: string;
  label: string;
};

/**
 * The detail pane's tabs.
 *
 * ⚠️ "Service targets" sits HERE rather than in Settings, and the placement is the decision.
 * Settings is gated on settings.manage, which also carries every feature flag in the product,
 * so a team administrator setting their own team's response times from there would have needed
 * the switches for the whole application. This tab is gated on sla.manage and the server checks
 * it against the team. The organisation-wide defaults and the working week stay in Settings,
 * because editing the calendar moves every team's deadlines at once.
 */
export type TeamTab = "categories" | "members" | "targets" | "links";

export const TEAM_TABS: readonly TeamTab[] = ["categories", "members", "targets", "links"];

/**
 * The part of `/admin`'s search this panel reads and writes.
 *
 * ⚠️ Declared here rather than imported from the route, which already imports this file —
 * a type-only import back would be legal but circular, and the route is the wrong place to
 * look for what a panel touches. The route still validates all three; this only describes
 * the two that belong to this screen, and spreads the rest through untouched.
 */
interface AdminTeamSearch {
  team?: string;
  teamTab?: TeamTab;
}

/**
 * Support teams — the teams themselves, the categories they own, and who is on them.
 *
 * ⚠️ All three are PHI access decisions wearing the clothes of reference data.
 *
 * A team's clearance is what its members may read — changing it adds or removes access for
 * every member at once, retroactively. A category's sensitivity is the classification both
 * enforcement layers gate on, and reclassifying one changes who can read every request
 * already in it. Membership is what confers the clearance to begin with. The screen says so
 * at the point of each decision rather than leaving it to be inferred from a dropdown label.
 *
 * ⚠️ Membership used to live on its own tab, and merging it here is a correctness fix rather
 * than tidying: an administrator could add somebody to EMR without the word "Clinical"
 * appearing anywhere on the screen they were looking at. Now the clearance a team carries
 * sits directly above the control that grants it. THAT PROPERTY IS LOAD-BEARING and survives
 * the master-detail layout below — the clearance banner is rendered by the detail pane, above
 * the tabs, so it is on screen whichever tab is open.
 *
 * ⚠️ The layout is master-detail rather than a list of expanding cards. Six teams carrying
 * ninety categories between them meant the open team's categories pushed everything else off
 * the screen, and finding one category was a scroll through several teams' worth of rows. The
 * left rail is now always the whole set of teams and the right pane is one team at a time.
 *
 * ⚠️ Team writes and category writes are gated SEPARATELY, on team.manage and category.manage.
 * They were not: the tab was shown on team.manage alone, so a holder of one without the other
 * saw every category control and collected a 403 on each. The server was always right; the
 * screen was offering work it knew would be refused.
 *
 * ⚠️ Deleting is offered but narrowly. The server refuses the moment a request, member or
 * grant references the thing — a request whose team or category row is gone cannot say what
 * it was about, and for a category it cannot establish its own sensitivity, so it fails
 * closed and becomes unreadable to everyone. Anything that has handled work is CLOSED or
 * RETIRED instead, which removes it from every picker and leaves the history intact. What
 * delete is for is the team somebody created by mistake five minutes ago.
 */
export function TeamsPanel() {
  const [teams, setTeams] = useState<AdminTeam[] | null>(null);
  const [categories, setCategories] = useState<AdminCategory[]>([]);
  const [term, setTerm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [editingTeam, setEditingTeam] = useState<AdminTeam | "new" | null>(null);
  const [editingCategory, setEditingCategory] = useState<{
    category: AdminCategory | null;
    team: AdminTeam;
  } | null>(null);

  // -------------------------------------------------------------------------
  // Which team, and which of its tabs — both in the URL.
  //
  // ⚠️ The last two pieces of navigation in administration that were not addressable. "The
  // EMR team's response targets are wrong" could not be sent as a link, and a reload — which
  // happens on every quiet session refresh — dropped whoever was mid-task back to an empty
  // detail pane. Team ids are GUIDs, so this follows the ids-never-names rule the queue's own
  // filters do; a name has no business in a link somebody pastes into a chat window.
  //
  // ⚠️ Rendering only. Every panel behind these tabs re-checks server-side, and a team id
  // naming something this administrator cannot reach resolves to no selection rather than to
  // an error — `selected` is found in the list the server already filtered for them.
  // -------------------------------------------------------------------------
  const search = useSearch({ from: "/admin" });
  const navigate = useNavigate({ from: "/admin" });

  const selectedId = search.team ?? null;

  /**
   * ⚠️ Choosing a different team RESETS the tab, which is a safety behaviour rather than a
   * tidiness one and had to survive the move into the URL: without it somebody lands on
   * Members by inheritance from the last team they looked at, and edits the wrong membership.
   */
  const selectTeam = (id: string | null) =>
    void navigate({
      search: (current: AdminTeamSearch) => ({
        ...current,
        team: id ?? undefined,
        teamTab: undefined,
      }),
      replace: true,
    });

  const innerTab: TeamTab = search.teamTab ?? "categories";

  const setInnerTab = (tab: TeamTab) =>
    void navigate({
      search: (current: AdminTeamSearch) => ({
        ...current,
        teamTab: tab === "categories" ? undefined : tab,
      }),
      replace: true,
    });

  // ⚠️ Separate permissions, because they are separate grants on the server. Reading the
  // screen needs either; each half of it is written under its own.
  const canManageTeams = useCan(Permission.TeamManage);
  const canManageCategories = useCan(Permission.CategoryManage);

  // ⚠️ Permissions reach the browser WITHOUT their scope, so this is true for a team
  // administrator who may edit exactly one team's targets. It decides whether the tab is
  // drawn; the server decides which team it may be used on, and answers 404 otherwise.
  const canManageSla = useCan(Permission.SlaManage);

  const load = async () => {
    try {
      const [t, c] = await Promise.all([
        // ⚠️ /api/admin/teams, not /api/teams. Same rows, but gated on team.manage and
        // carrying the counts — the plain list is reachable by every requester and
        // deliberately says nothing about headcount.
        api<AdminTeam[]>("/api/admin/teams"),
        api<AdminCategory[]>("/api/categories"),
      ]);
      setTeams(t);
      setCategories(c);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load teams.");
      setTeams([]);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const byTeam = useMemo(() => {
    const map = new Map<string, AdminCategory[]>();
    for (const c of categories) {
      const list = map.get(c.teamId) ?? [];
      list.push(c);
      map.set(c.teamId, list);
    }
    return map;
  }, [categories]);

  // ⚠️ A team's CATEGORY NAMES are searchable as well as its own, because that is how somebody
  // arrives here: they know the request was filed under "Password reset" and need the team that
  // owns it. A category belongs to exactly one team (D-71), so the answer is unambiguous.
  const matches = useListSearch(teams, term, (t) => [
    t.name,
    t.description,
    (byTeam.get(t.id) ?? []).map((c) => c.name).join(" "),
  ]);

  async function setTeamActive(team: AdminTeam, isActive: boolean) {
    setError(null);

    try {
      await api(`/api/teams/${team.id}/active`, { method: "PUT", body: { isActive } });
      await load();
    } catch (e) {
      // Verbatim: the server says how many open requests stand in the way, which is what
      // the administrator needs before they can act.
      setError(e instanceof ApiError ? e.message : "That team was not changed.");
    }
  }

  async function setCategoryActive(category: AdminCategory, isActive: boolean) {
    setError(null);

    try {
      await api(`/api/categories/${category.id}/active`, { method: "PUT", body: { isActive } });
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "That category was not changed.");
    }
  }

  /**
   * Delete, which the server allows only when nothing references the thing.
   *
   * Both endpoints refuse the moment a request, member or grant points at it, and say what
   * is in the way. That refusal is the real control — this just asks, and shows the answer.
   */
  async function remove(kind: "teams" | "categories", id: string, label: string) {
    setError(null);

    try {
      await api(`/api/${kind}/${id}`, { method: "DELETE" });
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : `${label} could not be deleted.`);
    }
  }

  if (teams === null) return <PanelSkeleton />;

  if (editingTeam) {
    return (
      <TeamEditor
        team={editingTeam === "new" ? null : editingTeam}
        categories={editingTeam === "new" ? [] : (byTeam.get(editingTeam.id) ?? [])}
        onCancel={() => setEditingTeam(null)}
        onSaved={async () => {
          setEditingTeam(null);
          await load();
        }}
      />
    );
  }

  if (editingCategory) {
    return (
      <CategoryEditor
        category={editingCategory.category}
        team={editingCategory.team}
        onCancel={() => setEditingCategory(null)}
        onSaved={async () => {
          setEditingCategory(null);
          await load();
        }}
      />
    );
  }

  const selected = teams.find((t) => t.id === selectedId) ?? null;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {teams.length} {teams.length === 1 ? "team" : "teams"} · {categories.length} categories ·
          every team supports all sites (D-14)
        </p>

        {canManageTeams && (
          <button
            type="button"
            onClick={() => setEditingTeam("new")}
            className="inline-flex h-9 items-center gap-2 rounded-lg bg-primary px-3 text-sm font-medium text-primary-foreground transition hover:opacity-90"
          >
            <Plus className="h-4 w-4" aria-hidden />
            New team
          </button>
        )}
      </div>

      {error && (
        <div className="mt-3">
          <ErrorNote message={error} />
        </div>
      )}

      {/* Master-detail. On a narrow screen exactly one side is on show, which is why both
          panes carry a hidden class rather than the grid collapsing — a rail and a detail
          stacked vertically on a phone puts the team you are editing below six you are not. */}
      <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,17rem)_minmax(0,1fr)]">
        <nav aria-label="Support teams" className={cn("min-w-0", selected && "hidden lg:block")}>
          {/* ⚠️ In the RAIL rather than above the whole master-detail, because it narrows the
              rail and nothing else. A search box spanning both panes would read as filtering
              the team on screen beside it.

              ⚠️ The selected team can be searched away, and that is correct: the detail pane
              keeps rendering it, so nothing is lost — clearing the box brings the rail back.
              Hiding a selection from a list while it is on screen is what would be confusing. */}
          <ListSearch
            value={term}
            onChange={setTerm}
            label="teams"
            placeholder="Search teams…"
            total={teams.length}
            showing={matches.length}
            threshold={6}
          />

          <ul className="space-y-1">
            {matches.map((t) => {
              const own = byTeam.get(t.id) ?? [];
              const isSelected = t.id === selectedId;

              return (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => {
                      // Resets the tab to Categories as part of the same navigation — see
                      // `selectTeam`, which is where that safety behaviour now lives.
                      selectTeam(t.id);

                      // A confirmation belongs to the team it was opened on. Carrying one
                      // across a selection is how the wrong thing gets deleted.
                      setPendingAction(null);
                    }}
                    aria-current={isSelected ? "true" : undefined}
                    className={cn(
                      "w-full rounded-lg border px-3 py-2.5 text-left transition",
                      isSelected
                        ? "border-ring bg-secondary"
                        : "border-transparent hover:border-border hover:bg-secondary/50",
                    )}
                  >
                    <span className="flex items-center gap-2">
                      <Layers className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                      <span className="min-w-0 flex-1 truncate text-sm font-medium">{t.name}</span>

                      {/* ⚠️ A team nobody is on cannot work its queue: both layers require
                          clearance before any scope, and clearance comes from membership. It
                          is flagged in the rail so it is visible without opening every team. */}
                      {t.memberCount === 0 && t.isActive && (
                        <AlertTriangle
                          className="h-3.5 w-3.5 shrink-0 text-warning"
                          aria-label="Nobody can work this queue"
                        />
                      )}

                      {!t.isActive && (
                        <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                          Closed
                        </span>
                      )}
                    </span>

                    <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                      {t.memberCount} {t.memberCount === 1 ? "member" : "members"} · {own.length}{" "}
                      {own.length === 1 ? "category" : "categories"}
                      {t.openTicketCount > 0 && <> · {t.openTicketCount} open</>}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className={cn("min-w-0", !selected && "hidden lg:block")}>
          {selected ? (
            <TeamDetail
              team={selected}
              categories={byTeam.get(selected.id) ?? []}
              innerTab={innerTab}
              onInnerTab={setInnerTab}
              pendingAction={pendingAction}
              onPendingAction={setPendingAction}
              canManageTeams={canManageTeams}
              canManageCategories={canManageCategories}
              canManageSla={canManageSla}
              onBack={() => selectTeam(null)}
              onEditTeam={() => setEditingTeam(selected)}
              onNewCategory={() => setEditingCategory({ category: null, team: selected })}
              onEditCategory={(c) => setEditingCategory({ category: c, team: selected })}
              onSetTeamActive={setTeamActive}
              onSetCategoryActive={setCategoryActive}
              onRemove={remove}
              onChanged={load}
            />
          ) : (
            <div className="rounded-xl border border-dashed border-border p-10 text-center">
              <Layers className="mx-auto h-8 w-8 text-muted-foreground" aria-hidden />
              <p className="mt-3 text-sm text-muted-foreground">
                Choose a team to manage its categories, its members and what they may read.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * One team: what it clears, what it owns, and who is on it.
 *
 * ⚠️ The clearance banner is rendered ABOVE the tabs rather than inside the Members tab, so
 * "membership of this team is clearance to read these classifications" is on screen whichever
 * tab is open. That was the whole reason membership stopped being a separate screen, and a
 * master-detail layout would quietly undo it if the banner travelled with the member list.
 */
function TeamDetail({
  team,
  categories,
  innerTab,
  onInnerTab,
  pendingAction,
  onPendingAction,
  canManageTeams,
  canManageCategories,
  canManageSla,
  onBack,
  onEditTeam,
  onNewCategory,
  onEditCategory,
  onSetTeamActive,
  onSetCategoryActive,
  onRemove,
  onChanged,
}: {
  team: AdminTeam;
  categories: AdminCategory[];
  innerTab: TeamTab;
  onInnerTab: (tab: TeamTab) => void;
  pendingAction: PendingAction | null;
  onPendingAction: (action: PendingAction | null) => void;
  canManageTeams: boolean;
  canManageCategories: boolean;
  canManageSla: boolean;
  onBack: () => void;
  onEditTeam: () => void;
  onNewCategory: () => void;
  onEditCategory: (category: AdminCategory) => void;
  onSetTeamActive: (team: AdminTeam, isActive: boolean) => Promise<void>;
  onSetCategoryActive: (category: AdminCategory, isActive: boolean) => Promise<void>;
  onRemove: (kind: "teams" | "categories", id: string, label: string) => Promise<void>;
  onChanged: () => Promise<void>;
}) {
  return (
    <section className="rounded-xl border border-border bg-card">
      <div className="p-4">
        <button
          type="button"
          onClick={onBack}
          className="mb-3 inline-flex items-center gap-1 text-xs text-muted-foreground transition hover:text-foreground lg:hidden"
        >
          <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
          All teams
        </button>

        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="flex flex-wrap items-center gap-2 font-medium">
              <Layers className="h-4 w-4 text-muted-foreground" aria-hidden />
              {team.name}
              {!team.isActive && (
                <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                  Closed
                </span>
              )}
            </h2>
            {team.description && (
              <p className="mt-0.5 text-sm text-muted-foreground">{team.description}</p>
            )}
          </div>

          {canManageTeams && (
            <div className="flex shrink-0 gap-2">
              <button
                type="button"
                onClick={onEditTeam}
                className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-input px-2.5 text-sm transition hover:border-ring"
              >
                <Pencil className="h-4 w-4" aria-hidden />
                <span className="hidden sm:inline">Edit</span>
              </button>

              {/* Closing asks; reopening does not. Only one direction takes a team out
                  of every assignment picker. */}
              <button
                type="button"
                onClick={() => {
                  if (team.isActive) {
                    onPendingAction({ kind: "close-team", id: team.id, label: team.name });
                    return;
                  }

                  void onSetTeamActive(team, true);
                }}
                className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-input px-2.5 text-sm text-muted-foreground transition hover:border-ring hover:text-foreground"
              >
                <Power className="h-4 w-4" aria-hidden />
                <span className="hidden sm:inline">{team.isActive ? "Close" : "Reopen"}</span>
              </button>

              {/* Offered unconditionally rather than pre-disabled: the client cannot
                  know how many requests a team has handled without asking, and the
                  server's refusal names what is in the way, which is more useful than
                  a greyed-out button with no explanation. */}
              <button
                type="button"
                onClick={() =>
                  onPendingAction({ kind: "delete-team", id: team.id, label: team.name })
                }
                aria-label={`Delete ${team.name}`}
                className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-input px-2.5 text-sm text-muted-foreground transition hover:border-destructive/50 hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" aria-hidden />
                <span className="hidden sm:inline">Delete</span>
              </button>
            </div>
          )}
        </div>

        {pendingAction?.id === team.id && pendingAction.kind === "close-team" && (
          <ConfirmPanel
            tone="warning"
            question={`Close the team "${team.name}"?`}
            detail={`It leaves every assignment picker immediately. Its ${team.openTicketCount} open ${team.openTicketCount === 1 ? "request stays open" : "requests stay open"} and its members keep the clearance the team gives them. You can reopen it at any time.`}
            confirmLabel="Close team"
            onConfirm={async () => {
              await onSetTeamActive(team, false);
              onPendingAction(null);
            }}
            onCancel={() => onPendingAction(null)}
          />
        )}

        {pendingAction?.id === team.id && pendingAction.kind === "delete-team" && (
          <ConfirmPanel
            question={`Delete the team "${team.name}" permanently?`}
            detail="It cannot be undone. Members lose the clearance this team gives them, and the server refuses outright if any request, member or grant still points at it — close it instead if you only want it out of the pickers."
            confirmLabel="Delete team"
            onConfirm={async () => {
              await onRemove("teams", team.id, `'${team.name}'`);
              onPendingAction(null);
            }}
            onCancel={() => onPendingAction(null)}
          />
        )}

        {/* Said plainly, above the tabs — membership of this team IS clearance to read
            these classifications, whichever tab happens to be open. */}
        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg bg-muted/50 px-3 py-2">
          <ShieldCheck className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          <span className="text-xs text-muted-foreground">Members may read</span>
          {team.clearedLevels.length === 0 ? (
            <span className="text-xs font-medium">nothing — this team clears no level</span>
          ) : (
            team.clearedLevels.map((l) => <LevelChip key={l} level={l} />)
          )}
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <Users className="h-3.5 w-3.5" aria-hidden />
            {team.memberCount} {team.memberCount === 1 ? "member" : "members"}
          </span>

          <span className="inline-flex items-center gap-1">
            <Tag className="h-3.5 w-3.5" aria-hidden />
            {team.categoryCount} {team.categoryCount === 1 ? "category" : "categories"}
          </span>

          {team.openTicketCount > 0 && (
            <span className="inline-flex items-center gap-1">
              <Inbox className="h-3.5 w-3.5" aria-hidden />
              {team.openTicketCount} open
            </span>
          )}

          {team.memberCount === 0 && team.isActive && (
            <span className="inline-flex items-center gap-1 text-warning">
              <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
              Nobody can work this queue
            </span>
          )}
        </div>
      </div>

      <div className="border-t border-border">
        <div
          role="tablist"
          aria-label={`${team.name} detail`}
          className="flex gap-1 border-b border-border px-4 pt-3"
        >
          {(
            [
              ["categories", "Categories", categories.length],
              ["members", "Members", team.memberCount],

              // ⚠️ Drawn only where the permission is held, and null rather than a count
              // because "how many targets" is not a number anybody wants — every priority
              // always has one; what varies is how many are set here rather than inherited,
              // and that is per category, which the panel itself shows.
              ...(canManageSla ? ([["targets", "Service targets", null]] as const) : []),

              // ⚠️ NO COUNT, and this one is a gap rather than a decision. "How many other teams
              // can read this team's requests" is a number an administrator would want at a
              // glance — a link is the only thing on this screen that widens who reads a team's
              // PHI-bearing requests without appearing in the member list. The teams payload does
              // not carry it, and adding a field to it is a separate change from adding the tab;
              // until then the panel itself is where the answer is.
              //
              // ⚠️ Gated on team.manage rather than sla.manage: a link is membership-shaped, not
              // a commitment. Reaching this screen at all needs team.manage, so the tab is drawn
              // for anybody here — and the server checks it again, per team, on every route.
              ["links", "Linked teams", null],
            ] as readonly (readonly [TeamTab, string, number | null])[]
          ).map(([id, label, count]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={innerTab === id}
              onClick={() => onInnerTab(id)}
              className={cn(
                "inline-flex h-9 items-center gap-2 rounded-t-lg border-b-2 px-3 text-sm transition",
                innerTab === id
                  ? "border-primary font-medium text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {label}
              <span
                className={cn(
                  "rounded-full bg-secondary px-1.5 text-[11px] text-secondary-foreground",
                  count === null && "hidden",
                )}
              >
                {count}
              </span>
            </button>
          ))}
        </div>

        <div className="p-4">
          {innerTab === "categories" && (
            <div>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Categories
                </h3>

                {canManageCategories ? (
                  <button
                    type="button"
                    onClick={onNewCategory}
                    className="inline-flex h-9 items-center gap-2 rounded-lg border border-input px-3 text-sm transition hover:border-ring"
                  >
                    <Plus className="h-4 w-4" aria-hidden />
                    New category
                  </button>
                ) : (
                  /* ⚠️ Said rather than hidden. A category list with no controls and no
                     explanation reads as a broken screen, and the reason is actionable:
                     it is a permission somebody can be granted. */
                  <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Lock className="h-3.5 w-3.5" aria-hidden />
                    Read-only — category.manage is needed to change these
                  </span>
                )}
              </div>

              {categories.length === 0 ? (
                <p className="mt-3 text-sm text-muted-foreground">
                  No categories yet. Requests cannot be routed to this team until it has at least
                  one.
                </p>
              ) : (
                <ul className="mt-3 divide-y divide-border">
                  {categories.map((c) => (
                    <li key={c.id} className="flex flex-wrap items-center gap-2 py-2">
                      <Tag className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
                      <span
                        className={cn(
                          "min-w-0 flex-1 truncate text-sm",
                          !c.isActive && "text-muted-foreground line-through",
                        )}
                      >
                        {c.name}
                      </span>

                      <LevelChip level={c.sensitivity} />

                      {c.excludeFromAiAssistant && (
                        <span
                          className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground"
                          title="The new-request wizard skips TARS for this category"
                        >
                          <BotOff className="h-3 w-3" aria-hidden />
                          No AI
                        </span>
                      )}

                      {canManageCategories && (
                        <>
                          <button
                            type="button"
                            onClick={() => onEditCategory(c)}
                            className="inline-flex h-8 items-center gap-1 rounded-lg border border-input px-2 text-xs transition hover:border-ring"
                          >
                            <Pencil className="h-3.5 w-3.5" aria-hidden />
                            Edit
                          </button>

                          <button
                            type="button"
                            onClick={() => {
                              if (c.isActive) {
                                onPendingAction({
                                  kind: "retire-category",
                                  id: c.id,
                                  label: c.name,
                                });
                                return;
                              }

                              void onSetCategoryActive(c, true);
                            }}
                            className="inline-flex h-8 items-center gap-1 rounded-lg border border-input px-2 text-xs text-muted-foreground transition hover:border-ring hover:text-foreground"
                          >
                            <Power className="h-3.5 w-3.5" aria-hidden />
                            {c.isActive ? "Retire" : "Reopen"}
                          </button>

                          <button
                            type="button"
                            onClick={() =>
                              onPendingAction({
                                kind: "delete-category",
                                id: c.id,
                                label: c.name,
                              })
                            }
                            aria-label={`Delete ${c.name}`}
                            className="inline-flex h-8 items-center gap-1 rounded-lg border border-input px-2 text-xs text-muted-foreground transition hover:border-destructive/50 hover:text-destructive"
                          >
                            <Trash2 className="h-3.5 w-3.5" aria-hidden />
                            Delete
                          </button>
                        </>
                      )}

                      {pendingAction?.id === c.id && (
                        <div className="w-full">
                          {pendingAction.kind === "retire-category" ? (
                            <ConfirmPanel
                              tone="warning"
                              question={`Retire the category "${c.name}"?`}
                              detail="It disappears from the new-request form immediately. Requests already filed under it keep it and stay where they are."
                              confirmLabel="Retire category"
                              onConfirm={async () => {
                                await onSetCategoryActive(c, false);
                                onPendingAction(null);
                              }}
                              onCancel={() => onPendingAction(null)}
                            />
                          ) : (
                            <ConfirmPanel
                              question={`Delete the category "${c.name}" permanently?`}
                              detail={`It cannot be undone, and its ${c.sensitivity} classification goes with it. The server refuses if any request still points at it — retire it instead if you only want it off the form.`}
                              confirmLabel="Delete category"
                              onConfirm={async () => {
                                await onRemove("categories", c.id, `'${c.name}'`);
                                onPendingAction(null);
                              }}
                              onCancel={() => onPendingAction(null)}
                            />
                          )}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* ⚠️ Still under the same clearance banner, which is the part that matters.
              Adding somebody here is what lets them read the classifications shown above;
              the old separate TAB meant an administrator could grant clinical access
              without the word "Clinical" ever being on screen.

              onChanged refreshes the parent so the member count on the rail and the tab
              badge follow immediately — a stale count is what decides whether Delete
              looks available. */}
          {innerTab === "members" && <TeamMembers team={team} onChanged={onChanged} />}

          {/* ⚠️ Guarded on the permission as well as the tab. The tab is not drawn without it,
              but a tab is a rendering decision and this state survives switching teams — the
              guard is what makes forcing it impossible rather than merely awkward. The server
              refuses regardless, per team. */}
          {innerTab === "targets" && canManageSla && <TeamSlaPanel teamId={team.id} />}

          {/* ⚠️ Under the same clearance banner as the members tab, and that is the part that
              matters here too (D-97). A link decides which OTHER team's agents read this team's
              requests, so it belongs on the screen that already states what those requests are
              classified as — the panel itself then says which of those classifications the link
              actually reaches, because reach without clearance is a live link that shows nothing. */}
          {innerTab === "links" && <TeamLinksPanel teamId={team.id} />}
        </div>
      </div>
    </section>
  );
}

function TeamEditor({
  team,
  categories,
  onCancel,
  onSaved,
}: {
  team: AdminTeam | null;
  categories: AdminCategory[];
  onCancel: () => void;
  onSaved: () => Promise<void>;
}) {
  const [name, setName] = useState(team?.name ?? "");
  const [description, setDescription] = useState(team?.description ?? "");
  const [levels, setLevels] = useState<string[]>(team?.clearedLevels ?? ["Standard"]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Classifications this team's own categories carry. Unticking one would leave the team
  // unable to read its own queue, and the server refuses it — so the control says so first
  // rather than letting the save fail.
  const required = useMemo(
    () => [...new Set(categories.filter((c) => c.isActive).map((c) => c.sensitivity))],
    [categories],
  );

  const gaining = team ? levels.filter((l) => !team.clearedLevels.includes(l)) : levels;
  const losing = team ? team.clearedLevels.filter((l) => !levels.includes(l)) : [];

  async function submit() {
    setSaving(true);
    setError(null);

    const body = { name: name.trim(), description: description.trim(), clearedLevels: levels };

    try {
      if (team) await api(`/api/teams/${team.id}`, { method: "PUT", body });
      else await api("/api/teams", { method: "POST", body });

      await onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "That team could not be saved.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="rounded-2xl border border-border bg-card">
      <header className="border-b border-border p-4">
        <h2 className="text-lg font-semibold">{team ? `Edit ${team.name}` : "New team"}</h2>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Teams are global — every team supports all sites. A request's site says where the
          requester is, not who may read it.
        </p>
      </header>

      {error && (
        <div className="border-b border-border p-4">
          <ErrorNote message={error} />
        </div>
      )}

      <div className="space-y-6 p-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="team-name" className="mb-1 block text-xs font-medium">
              Name
            </label>
            <input
              id="team-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-10 w-full rounded-lg border border-input bg-background px-3 text-base focus:border-ring focus:outline-none md:text-sm"
            />
          </div>

          <div>
            <label htmlFor="team-description" className="mb-1 block text-xs font-medium">
              Description
            </label>
            <input
              id="team-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="h-10 w-full rounded-lg border border-input bg-background px-3 text-base focus:border-ring focus:outline-none md:text-sm"
            />
          </div>
        </div>

        <fieldset>
          <legend className="text-sm font-medium">Clearance</legend>
          <p className="mt-0.5 text-sm text-muted-foreground">
            What every member of this team may read. A set, not a level — HR clears Sensitive but
            never Clinical, EMR the reverse.
          </p>

          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {SENSITIVITY_LEVELS.map((level) => {
              const checked = levels.includes(level);
              const isRequired = required.includes(level);

              return (
                <label
                  key={level}
                  className={cn(
                    "flex cursor-pointer gap-3 rounded-lg border p-3 transition",
                    checked ? "border-ring bg-secondary/50" : "border-border hover:border-ring/50",
                  )}
                >
                  <Checkbox
                    className="mt-0.5"
                    checked={checked}
                    onChange={() =>
                      setLevels((current) =>
                        current.includes(level)
                          ? current.filter((l) => l !== level)
                          : [...current, level],
                      )
                    }
                  />
                  <span className="min-w-0">
                    <LevelChip level={level} />
                    <span className="mt-1 block text-xs text-muted-foreground">
                      {LEVEL_DESCRIPTIONS[level as SensitivityLevel]}
                    </span>
                    {isRequired && !checked && (
                      <span className="mt-1 block text-xs text-destructive">
                        This team owns {level} categories. Without it, its own members could not
                        read them.
                      </span>
                    )}
                  </span>
                </label>
              );
            })}
          </div>

          {(gaining.length > 0 || losing.length > 0) && (
            <p className="mt-3 flex items-start gap-2 text-xs text-warning">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
              <span>
                {gaining.length > 0 && (
                  <>
                    Every current member gains access to {gaining.join(", ")} requests — including
                    ones already raised.{" "}
                  </>
                )}
                {losing.length > 0 && (
                  <>Every current member loses access to {losing.join(", ")} requests. </>
                )}
                Recorded against your name in the activity log.
              </span>
            </p>
          )}
        </fieldset>
      </div>

      <footer className="flex flex-wrap justify-end gap-2 border-t border-border p-4">
        <button
          type="button"
          onClick={onCancel}
          className="h-10 rounded-lg border border-input px-4 text-sm transition hover:border-ring"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void submit()}
          disabled={saving || !name.trim()}
          className="h-10 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground transition hover:opacity-90 disabled:opacity-50"
        >
          {saving ? "Saving…" : team ? "Save team" : "Create team"}
        </button>
      </footer>
    </section>
  );
}

function CategoryEditor({
  category,
  team,
  onCancel,
  onSaved,
}: {
  category: AdminCategory | null;
  team: AdminTeam;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}) {
  const [name, setName] = useState(category?.name ?? "");
  const [sensitivity, setSensitivity] = useState(
    category?.sensitivity ?? team.clearedLevels[0] ?? "Standard",
  );
  const [excludeFromAiAssistant, setExcludeFromAiAssistant] = useState(
    category?.excludeFromAiAssistant ?? false,
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const reclassifying = category !== null && category.sensitivity !== sensitivity;

  async function submit() {
    setSaving(true);
    setError(null);

    try {
      if (category) {
        await api(`/api/categories/${category.id}`, {
          method: "PUT",
          body: { name: name.trim(), sensitivity, excludeFromAiAssistant },
        });
      } else {
        await api("/api/categories", {
          method: "POST",
          body: { teamId: team.id, name: name.trim(), sensitivity, excludeFromAiAssistant },
        });
      }

      await onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "That category could not be saved.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="rounded-2xl border border-border bg-card">
      <header className="border-b border-border p-4">
        <h2 className="text-lg font-semibold">
          {category ? `Edit ${category.name}` : `New category on ${team.name}`}
        </h2>
        <p className="mt-0.5 text-sm text-muted-foreground">
          A category's classification is what both enforcement layers gate on. It cannot be moved to
          another team — that would move every request in it.
        </p>
      </header>

      {error && (
        <div className="border-b border-border p-4">
          <ErrorNote message={error} />
        </div>
      )}

      <div className="space-y-4 p-4">
        <div>
          <label htmlFor="category-name" className="mb-1 block text-xs font-medium">
            Name
          </label>
          <input
            id="category-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="h-10 w-full rounded-lg border border-input bg-background px-3 text-base focus:border-ring focus:outline-none md:text-sm"
          />
        </div>

        <fieldset>
          <legend className="mb-1 text-xs font-medium">Classification</legend>

          {/* Only what the owning team clears. A category its own team cannot read would be
              invisible to the very people meant to work it, and the server refuses it. */}
          <div className="space-y-2">
            {team.clearedLevels.map((level) => (
              <label
                key={level}
                className={cn(
                  "flex cursor-pointer gap-3 rounded-lg border p-3 transition",
                  sensitivity === level
                    ? "border-ring bg-secondary/50"
                    : "border-border hover:border-ring/50",
                )}
              >
                <input
                  type="radio"
                  name="sensitivity"
                  checked={sensitivity === level}
                  onChange={() => setSensitivity(level)}
                  className="mt-0.5 h-4 w-4 shrink-0"
                />
                <span className="min-w-0">
                  <LevelChip level={level} />
                  <span className="mt-1 block text-xs text-muted-foreground">
                    {LEVEL_DESCRIPTIONS[level as SensitivityLevel]}
                  </span>
                </span>
              </label>
            ))}
          </div>

          <p className="mt-2 text-xs text-muted-foreground">
            Only levels {team.name} is cleared for are offered. Widen the team's clearance first if
            you need another.
          </p>
        </fieldset>

        <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3 hover:border-ring/50">
          <input
            type="checkbox"
            checked={excludeFromAiAssistant}
            onChange={(e) => setExcludeFromAiAssistant(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0"
          />
          <span className="min-w-0">
            <span className="text-sm font-medium">Skip the ticket assistant for this category</span>
            <span className="mt-1 block text-xs text-muted-foreground">
              Off by default. Turn this on for a category whose process is a scripted intake TARS
              would only get in the way of — a new hire's equipment request, a termination's
              access-removal checklist. The wizard goes straight to the plain form instead.
            </span>
          </span>
        </label>

        {reclassifying && (
          <p className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
            Reclassifying is retroactive. Moving this from {category.sensitivity} to {sensitivity}{" "}
            changes who can read every request already in it, not just future ones. Recorded at high
            severity with the number affected.
          </p>
        )}
      </div>

      <footer className="flex flex-wrap justify-end gap-2 border-t border-border p-4">
        <button
          type="button"
          onClick={onCancel}
          className="h-10 rounded-lg border border-input px-4 text-sm transition hover:border-ring"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void submit()}
          disabled={saving || !name.trim()}
          className="h-10 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground transition hover:opacity-90 disabled:opacity-50"
        >
          {saving ? "Saving…" : category ? "Save category" : "Create category"}
        </button>
      </footer>
    </section>
  );
}
